'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { redirectToAuth } from 'supertokens-auth-react';
import SuperTokens from 'supertokens-auth-react/ui';

import { PreBuiltUIList } from '@/services/config/frontend';

import { AuthComponent } from './components/auth-component';
import { AuthFormSkeleton } from './components/auth-form-skeleton';
import { CustomerNoToken } from './components/customer-no-token';

function readSignUpTokens() {
  return Boolean(
    sessionStorage.getItem('customerRegistrationToken') ||
    sessionStorage.getItem('collaboratorInvitationToken')
  );
}

export default function Auth() {
  /*
   * SuperTokens recipes are initialised browser-side only (see supertokens-provider).
   * Reading routing state at render time crashes during Next.js's pre-render.
   */
  const [canHandleRoute, setCanHandleRoute] = useState<boolean | null>(null);
  /*
   * null = not yet resolved (SSR / before first effect). After mount this is set
   * synchronously from sessionStorage so it never stays null past the first paint.
   */
  const [hasSignUpToken, setHasSignUpToken] = useState<boolean | null>(null);
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();

  const isSignUpPath =
    pathname.includes('signup') || searchParams.get('show') === 'signup';

  /*
   * Scoped to the sign-up route on purpose: SuperTokens' own routes consume a
   * `token` query param straight off the live URL (/reset-password,
   * /verify-email), so stripping it anywhere else silently breaks them.
   * Both tokens are stripped in one pass to avoid a second replace re-adding
   * one of them.
   */
  useEffect(() => {
    if (!isSignUpPath) return;

    const invitationToken = searchParams.get('invitationToken');
    const token = searchParams.get('token');
    if (!invitationToken && !token) return;

    if (invitationToken) {
      sessionStorage.setItem('collaboratorInvitationToken', invitationToken);
    }
    if (token) {
      sessionStorage.setItem('customerRegistrationToken', token);
    }

    const params = new URLSearchParams(searchParams.toString());
    params.delete('invitationToken');
    params.delete('token');
    router.replace(`${pathname}?${params}`);
  }, [isSignUpPath, pathname, router, searchParams]);

  /*
   * Both tokens are only consumed by the sign-up form. Clear them when leaving
   * so an abandoned invite can't attach to a later, unrelated sign-up in the
   * same tab. (A successful sign-up already clears them in getRedirectionURL.)
   */
  useEffect(() => {
    return () => {
      sessionStorage.removeItem('collaboratorInvitationToken');
      sessionStorage.removeItem('customerRegistrationToken');
    };
  }, []);

  /* Resolve token presence after URL intercept effects have run. */
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setHasSignUpToken(readSignUpTokens());
  }, [searchParams]);

  useEffect(() => {
    if (hasSignUpToken === null) return;

    /*
     * Skip SuperTokens routing when we will render CustomerNoToken — otherwise
     * canHandleRoute(false) triggers redirectToAuth before the guard can render.
     */
    if (isSignUpPath && !hasSignUpToken) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCanHandleRoute(true);
      return;
    }

    const handle = SuperTokens.canHandleRoute(PreBuiltUIList);
    setCanHandleRoute(handle);
    if (!handle) {
      redirectToAuth({ redirectBack: false }).catch(console.error);
    }
  }, [hasSignUpToken, isSignUpPath]);

  if (canHandleRoute !== true || hasSignUpToken === null)
    return <AuthFormSkeleton />;

  if (isSignUpPath && !hasSignUpToken) return <CustomerNoToken />;

  return <AuthComponent />;
}
